import React, { useCallback, useState } from 'react';
import { useSubjectLanguage } from '@features/user';
import { Send as SendIcon } from '@mui/icons-material';
import { Box, Popover, Typography } from '@mui/material';
import { capitalize } from '@utils/utils';
import removeMd from 'remove-markdown';

import type { BaseAction, CardContentProps } from '../../types';
import { ActionCommandType } from '../../types';

import CardContent from './base/CardContent';

const CONTAINER_NAME = 'pep-c-card';

interface PepCCardProps extends CardContentProps {
  action: BaseAction<'pep_c'>;
}

const PepCCard: React.FC<PepCCardProps> = ({ action, onCommand }) => {
  const actionData = action.action_payload;
  const subjectLanguage = useSubjectLanguage();
  const SubjectLanguage = capitalize(subjectLanguage);

  // State for preview popover only
  const [previewAnchorEl, setPreviewAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);

  // Get preview text for skim view (first 30-50 chars)
  const getPreviewText = () => {
    const plainText = removeMd(actionData.body);
    return plainText.length > 50 ? plainText.substring(0, 47) + '...' : plainText;
  };

  // Handle preview hover
  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (actionData.body) {
        const rect = event.currentTarget.getBoundingClientRect();
        setPreviewAnchorEl(event.currentTarget);
        setAnchorPosition({
          top: rect.top + rect.height / 2,
          left: rect.left - 82, // Adjust this value to move further left
        });
      }
    },
    [actionData.body],
  );

  const handleMouseLeave = useCallback(() => {
    setPreviewAnchorEl(null);
  }, []);

  // Content for the card
  const content = (
    <>
      <Typography variant="body2" color="text.secondary">
        {action.patient?.name}
      </Typography>
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          py: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {getPreviewText()}
        </Typography>
      </Box>
    </>
  );

  const handleCardClick = () => {
    onCommand({ type: ActionCommandType.EDIT_IN_MODAL });
  };

  return (
    <>
      <CardContent
        containerName={CONTAINER_NAME}
        id={action.action_id}
        title={`Send ${SubjectLanguage} Summary?`}
        content={content}
        avatarIcon={<SendIcon />}
        avatarColor="primary.main"
        onCommand={onCommand}
        expandable={false}
        showExpandIcon={true}
        isExpandableContentEditable={true}
        onCardClick={handleCardClick}
      />

      {/* Preview Popover for hover state */}
      <Popover
        open={Boolean(previewAnchorEl)}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition || undefined}
        onClose={() => {
          setPreviewAnchorEl(null);
          setAnchorPosition(null);
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: 'none',
          '& .MuiPopover-paper': {
            pointerEvents: 'none',
            maxWidth: 500,
            overflow: 'auto',
            borderRadius: 2,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="medium">
            {actionData.title}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
            {removeMd(actionData.body)}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default PepCCard;
